import { useState } from "react";
import { Link } from "react-router-dom";
import { useAsyncData } from "../hooks/useAsyncData";
import { useApi } from "../api/useApi";
import { blogService } from "../services/blog.service";
import { IBlogCard } from "../interfaces/blog/iBlogCard.interface";
import { BlogQueryVM } from "../interfaces/blog/iBlogQuery.interface";
import { BlogSortingEnum } from "../enums/blogSorting.enum";
import { BlogPostStatusEnum } from "../enums/blogPostStatus.enum";
import { PageContainer } from "../components/PageContainer";
import { EmptyState } from "../components/EmptyState";
import { SkeletonGrid } from "../components/SkeletonGrid";
import { PageSize } from "../const/page.const";
import { sectionCardCompactClasses, sectionTitleClasses } from "../styles/pageClasses";
import { gridClasses, primaryBtnClasses, secondaryBtnClasses } from "../styles/uiClasses";
import { userProfilePageClasses } from "../styles/pageSpecificClasses";

type BlogListResponse = {
  items: IBlogCard[];
  totalCount: number;
};

export default function AdminBlog() {
  const { request } = useApi();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<BlogSortingEnum>(BlogSortingEnum.New);
  const [page, setPage] = useState(1);

  const { data, loading, error } = useAsyncData<BlogListResponse>(
    () => {
      const query: BlogQueryVM = {
        q: q.trim() || undefined,
        sort,
        page,
        pageSize: PageSize,
      };
      return blogService.getAllAdmin(request, query);
    },
    [request, q, sort, page]
  );

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PageSize));

  return (
    <PageContainer maxWidth="7xl" contentClassName={userProfilePageClasses.shellSpacing}>
      <header className={userProfilePageClasses.header}>
        <div>
          <h1 className={userProfilePageClasses.title}>Admin Blog</h1>
          <p className={userProfilePageClasses.intro}>
            Create and update editorial content for the public blog section.
          </p>
        </div>
        <Link to="/admin/blog/create" className={`${primaryBtnClasses} ${userProfilePageClasses.primaryAction}`}>
          New Post
        </Link>
      </header>

      <div className={`${sectionCardCompactClasses} grid gap-3 sm:grid-cols-[1fr_auto]`}>
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="Search blog posts..."
          className="w-full rounded-2xl border border-[#d9ccb8] bg-white px-4 py-3 text-sm text-[#213547] outline-none transition focus:border-[#c96d3b]"
        />
        <select
          value={sort}
          onChange={(e) => {
            setSort(Number(e.target.value) as BlogSortingEnum);
            setPage(1);
          }}
          className="rounded-2xl border border-[#d9ccb8] bg-[#fffaf3] px-4 py-3 text-sm font-medium text-[#34566a] outline-none"
        >
          <option value={BlogSortingEnum.New}>Newest</option>
          <option value={BlogSortingEnum.Old}>Oldest</option>
          <option value={BlogSortingEnum.Featured}>Featured First</option>
          <option value={BlogSortingEnum.TitleAsc}>Title A-Z</option>
          <option value={BlogSortingEnum.TitleDesc}>Title Z-A</option>
        </select>
      </div>

      {error && <div className={userProfilePageClasses.error}>{error}</div>}

      {loading ? (
        <SkeletonGrid />
      ) : items.length === 0 ? (
        <EmptyState
          title="No blog posts yet"
          subtitle="Create your first post to start filling the blog section."
        />
      ) : (
        <>
          <section>
            <h2 className={`${sectionTitleClasses} ${userProfilePageClasses.sectionTitle}`}>Posts</h2>
            <div className={`${gridClasses} ${userProfilePageClasses.grid}`}>
              {items.map((item) => (
                <article key={item.id} className="rounded-[24px] border border-[#ddd1bf] bg-white p-5 shadow-sm">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3 text-xs uppercase tracking-[0.18em] text-[#a2572b]">
                      <span>{item.categoryName ?? "Uncategorized"}</span>
                      <span>
                        {item.blogPostStatusId === BlogPostStatusEnum.Published
                          ? "Published"
                          : item.blogPostStatusId === BlogPostStatusEnum.Draft
                            ? "Draft"
                            : "Archived"}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-[#213547]">{item.title}</h3>
                    {item.summary && <p className="text-sm leading-6 text-[#5c5b57]">{item.summary}</p>}
                    <div className="flex items-center justify-between text-sm text-[#5c5b57]">
                      <span>{item.readTimeMinutes ? `${item.readTimeMinutes} min read` : "Article"}</span>
                      <span>{item.isFeatured ? "Featured" : "Standard"}</span>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-3">
                    <Link to={`/admin/blog/preview/${item.id}`} className={secondaryBtnClasses}>
                      View
                    </Link>
                    <Link to={`/admin/blog/edit/${item.id}`} className={primaryBtnClasses}>
                      Edit
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className={secondaryBtnClasses}
              >
                Previous
              </button>
              <span className="text-sm text-[#5c5b57]">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className={primaryBtnClasses}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
